// Shared rules for the two "needs your attention" appointment queues:
// online bookings awaiting approval, and finished appointments still owed money.
//
// These used to live as private functions inside GroomsyCalendar. They are
// shared now because the dedicated Action Queue page renders the same two
// queues — if the rules were duplicated, the count on the sidebar badge and the
// list on the page could disagree with the calendar's own cards.

/**
 * Currency symbol for a groomer's configured currency.
 * Note ~42 publicGroomers docs still have no `currency` field, so the EUR
 * default here is load-bearing rather than theoretical.
 */
export function currencySymbol(c) {
  if (c === "GBP") return "£";
  if (c === "USD") return "$";
  return "€"; // default EUR
}

/** Normalizes the several date shapes an appointment can carry into a Date. */
export function getAppointmentStart(appt) {
  if (!appt) return null;
  const start =
    appt.date?.toDate?.() ??
    (appt.start
      ? new Date(appt.start)
      : appt.date
        ? new Date(appt.date)
        : null);
  if (!start || isNaN(start.getTime())) return null;
  return start;
}

export function isPendingOnlineBooking(appt) {
  if (!appt || appt.type === "blocked_time") return false;
  const status = String(appt?.status || "").toLowerCase();
  if (status !== "pending") return false;
  // Only true online bookings (not manually-entered ones) should appear in the
  // approval queue. Without the source check, manually-created appointments that
  // happen to have status "pending" would show up but have no Approve button.
  const source = String(appt?.source || "").toLowerCase();
  return source === "public_form" || source === "registration_form";
}

export function isReschedulePending(appt) {
  if (!appt || appt.type === "blocked_time") return false;
  return String(appt?.status || "").toLowerCase() === "reschedule_pending";
}

/** True for anything that belongs in the approval queue, either kind. */
export function isApprovalQueueAppointment(appt) {
  return isPendingOnlineBooking(appt) || isReschedulePending(appt);
}

export function isOutstandingPaymentAppointment(appt, now = new Date()) {
  if (!appt || appt.type === "blocked_time") return false;
  if (appt.payment?.waived) return false;

  const status = String(appt?.status || "confirmed").toLowerCase();
  if (status === "cancelled" || status === "declined") return false;

  const start = getAppointmentStart(appt);
  if (!start) return false;

  const durationMins = Number(appt.duration) || 60;
  const end = new Date(start.getTime() + durationMins * 60 * 1000);
  if (!(end < now)) return false;

  const total = Number(appt.price) || 0;
  const paid = Number(appt.payment?.amountPaid) || 0;
  return total > 0 && paid < total;
}

export function getDateValue(value) {
  if (!value) return null;
  const asDate = value?.toDate?.() ?? new Date(value);
  if (!asDate || isNaN(asDate.getTime())) return null;
  return asDate;
}

export function getOutstandingAmount(appt) {
  if (appt?.payment?.waived) return 0;
  const total = Number(appt?.price) || 0;
  const paid = Number(appt?.payment?.amountPaid) || 0;
  return Math.max(0, total - paid);
}

/**
 * Filters and orders one queue.
 * Pending sorts oldest-request-first (longest wait gets answered first);
 * outstanding sorts by largest balance, then by appointment date.
 */
export function buildQueueItems(appointments = [], filterKey) {
  if (!filterKey) return [];

  const now = new Date();
  const queue = appointments.filter((appt) => {
    if (filterKey === "pending") return isApprovalQueueAppointment(appt);
    if (filterKey === "outstanding") {
      return isOutstandingPaymentAppointment(appt, now);
    }
    return false;
  });

  queue.sort((a, b) => {
    if (filterKey === "pending") {
      const aRequested =
        getDateValue(a?.createdAt) || getAppointmentStart(a) || new Date(0);
      const bRequested =
        getDateValue(b?.createdAt) || getAppointmentStart(b) || new Date(0);
      return aRequested.getTime() - bRequested.getTime();
    }

    const dueDelta = getOutstandingAmount(b) - getOutstandingAmount(a);
    if (dueDelta !== 0) return dueDelta;

    const aStart = getAppointmentStart(a) || new Date(0);
    const bStart = getAppointmentStart(b) || new Date(0);
    return aStart.getTime() - bStart.getTime();
  });

  return queue;
}

export function countPendingApprovals(appointments = []) {
  return (appointments || []).filter(isApprovalQueueAppointment).length;
}

export function countOutstandingPayments(appointments = []) {
  const now = new Date();
  return (appointments || []).filter((a) =>
    isOutstandingPaymentAppointment(a, now),
  ).length;
}

export function sumOutstandingAmount(appointments = []) {
  const now = new Date();
  return (appointments || [])
    .filter((a) => isOutstandingPaymentAppointment(a, now))
    .reduce((total, a) => total + getOutstandingAmount(a), 0);
}

/** Reasons offered when declining a booking request. "Other" opens a free-text field. */
export const DECLINE_PRESETS = [
  "Fully booked",
  "Outside service area",
  "Service not available",
  "Schedule conflict",
  "Other",
];
